import type { GameState } from '../state';
import { D, add, sub, mul, pow, gte, type Decimal } from '../math/num';
import { getIntrusion, getGridUpgrade, COND_PER_RANK } from '../data/grid';

/**
 * Compute Grid sim (data/grid.ts holds the numbers). One intrusion runs at a
 * time off elapsedMs and auto-banks its Compute, identical online/offline.
 */

export interface GridOpResult {
  ok: boolean;
  reason?: string;
}

/** Resolve a finished intrusion (called from stepWorld). */
export function stepGrid(state: GameState, _dtMs: number): void {
  const g = state.grid;
  if (!g.unlocked || !g.run) return;
  if (state.elapsedMs < g.run.endsMs) return;
  const def = getIntrusion(g.run.targetId);
  if (def) {
    g.compute = add(g.compute, D(def.yields));
    g.intrusions += 1;
  }
  g.run = null;
}

export function startIntrusion(state: GameState, targetId: string): GridOpResult {
  const g = state.grid;
  if (!g.unlocked) return { ok: false, reason: 'The grid is not online.' };
  if (g.run) return { ok: false, reason: 'An intrusion is already running.' };
  const def = getIntrusion(targetId);
  if (!def) return { ok: false, reason: 'Unknown target.' };
  g.run = { targetId, endsMs: state.elapsedMs + def.durationMs };
  return { ok: true };
}

export function gridUpgradeCost(state: GameState, id: string): Decimal | null {
  const def = getGridUpgrade(id);
  if (!def) return null;
  const rank = state.grid.upgrades[def.id] ?? 0;
  return mul(D(def.baseCost), pow(D(def.costGrowth), rank));
}

/** Fraction of the booster stacking penalty that REMAINS after Conductance
 *  ranks (1 = untouched; falls toward 0 forever, never reaching it). */
export function boostConductance(state: GameState): number {
  const ranks = state.grid.upgrades['cond'] ?? 0;
  return Math.pow(1 - COND_PER_RANK, ranks);
}

/** Spend Compute on a permanent rank. CPU/Storage raise capacities live;
 *  Conductance eases the stacking penalty (read by the evaluator). */
export function buyGridUpgrade(state: GameState, id: string): GridOpResult {
  const g = state.grid;
  if (!g.unlocked) return { ok: false, reason: 'The grid is not online.' };
  const def = getGridUpgrade(id);
  if (!def) return { ok: false, reason: 'Unknown upgrade.' };
  const cost = gridUpgradeCost(state, id)!;
  if (!gte(g.compute, cost)) return { ok: false, reason: 'Not enough Compute.' };
  g.compute = sub(g.compute, cost);
  g.upgrades[def.id] = (g.upgrades[def.id] ?? 0) + 1;
  if (def.id === 'cpu' || def.id === 'storage') {
    state.resources[def.id] += def.amount;
    if (def.id === 'storage') state.inventory.cap += def.amount;
  }
  return { ok: true };
}

/** Bring the racks online — rides the MechCorp Data Center kill. */
export function unlockGrid(state: GameState): void {
  if (state.grid.unlocked) return;
  state.grid.unlocked = true;
  state.meta.unlocks.grid = true;
}
